import * as Sentry from '@sentry/react';

/**
 * src/lib/sentry.ts — Sentry initialisation (single source of truth).
 *
 * Called once, before ReactDOM.render in main.tsx.
 * Reads config from Vite environment variables (VITE_SENTRY_*).
 *
 * Features enabled:
 *  - Error tracking
 *  - Performance / distributed tracing (BrowserTracing)
 *  - Session Replay (canvas + network)
 *
 * Environment variables required (.env):
 *   VITE_SENTRY_DSN=https://xxx@oXXX.ingest.sentry.io/XXX
 *   VITE_SENTRY_ENV=development | staging | production
 *   VITE_SENTRY_TRACES_SAMPLE_RATE=1.0       (1.0 = 100% in dev; use 0.1 in prod)
 *   VITE_SENTRY_REPLAY_SAMPLE_RATE=0.1
 *   VITE_SENTRY_REPLAY_ERROR_SAMPLE_RATE=1.0
 */
export function initSentry() {
    const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

    if (!dsn) {
        console.warn('[Sentry] VITE_SENTRY_DSN not set — Sentry is disabled.');
        return;
    }

    Sentry.init({
        dsn,

        environment: (import.meta.env.VITE_SENTRY_ENV as string) ?? import.meta.env.MODE,

        integrations: [
            // Browser performance tracing (page loads, navigation, XHR, fetch)
            Sentry.browserTracingIntegration(),

            // Session Replay — records user sessions on errors
            Sentry.replayIntegration({
                maskAllText: true,       // mask PII in text nodes
                blockAllMedia: false,    // allow media in replay
            }),
        ],

        // Performance: % of transactions to send.
        // Use 1.0 locally, 0.1-0.2 in production.
        tracesSampleRate: parseFloat(
            (import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as string) ?? '1.0'
        ),

        // Replay: normal sessions sample rate
        replaysSessionSampleRate: parseFloat(
            (import.meta.env.VITE_SENTRY_REPLAY_SAMPLE_RATE as string) ?? '0.1'
        ),

        // Replay: always capture 100% of sessions that had an error
        replaysOnErrorSampleRate: parseFloat(
            (import.meta.env.VITE_SENTRY_REPLAY_ERROR_SAMPLE_RATE as string) ?? '1.0'
        ),

        // Ignore common non-actionable errors
        ignoreErrors: [
            'ResizeObserver loop limit exceeded',
            'Network request failed',
            /^ChunkLoadError/,
        ],

        // Attach "before-send" hook to scrub sensitive data before it leaves the browser
        beforeSend(event) {
            // Remove any password / token fields from extra context
            if (event.request?.data) {
                const data = event.request.data as Record<string, unknown>;
                ['password', 'token', 'accessToken', 'refreshToken'].forEach(key => {
                    if (key in data) data[key] = '[Filtered]';
                });
            }
            return event;
        },
    });
}
